/**
 * Fetch chatbot_log rows across the whole account with filters + pagination.
 * Mirrors useBotLogs but accepts `settingIds` for per-bot filtering.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import chatbotApi from "../../../api/chatbotApi";

const PAGE_SIZE = 50;

export const useAccountLogs = (accountId, filters = {}) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const { status, dateFrom, dateTo, search, settingIds, chatbotType } = filters;
  const filterKey = JSON.stringify({
    status: Array.isArray(status) ? status.slice().sort() : status,
    settingIds: Array.isArray(settingIds) ? settingIds.slice().sort() : settingIds,
    dateFrom,
    dateTo,
    search,
    chatbotType,
  });

  const fetchPage = useCallback(
    async ({ append = false } = {}) => {
      if (!accountId) {
        setRows([]);
        setTotal(0);
        setOffset(0);
        return;
      }

      const reqId = ++requestIdRef.current;
      const nextOffset = append ? offset + PAGE_SIZE : 0;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        const res = await chatbotApi.getLogsByAccount(accountId, {
          status,
          settingIds,
          dateFrom,
          dateTo,
          search,
          chatbotType,
          limit: PAGE_SIZE,
          offset: nextOffset,
        });
        if (reqId !== requestIdRef.current) return;
        if (res.status !== 1) {
          throw new Error(res.message || "Không tải được log");
        }
        setRows((prev) =>
          append ? [...prev, ...(res.data || [])] : res.data || [],
        );
        setTotal(res.total || 0);
        setOffset(nextOffset);
      } catch (err) {
        if (reqId !== requestIdRef.current) return;
        setError(err.message || String(err));
      } finally {
        if (reqId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId, filterKey],
  );

  useEffect(() => {
    fetchPage({ append: false });
  }, [fetchPage]);

  const loadMore = () => fetchPage({ append: true });
  const refresh = () => fetchPage({ append: false });
  const hasMore = offset + PAGE_SIZE < total;

  return {
    rows,
    total,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
    pageSize: PAGE_SIZE,
  };
};
